import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Button,
  Alert,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Table
} from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const ControlDetail = () => {
  const { id: sspId, controlId } = useParams();
  const navigate = useNavigate();
  const [control, setControl] = useState(null);
  const [formData, setFormData] = useState({
    implementationStatus: '',
    narrative: '',
    narrativeCompletion: '',
    responsibleRole: '',
    implementationNotes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [evidenceCount, setEvidenceCount] = useState(0);
  const [showEvidenceWarning, setShowEvidenceWarning] = useState(false);

  useEffect(() => {
    if (controlId) {
      fetchControl();
    }
  }, [controlId]);

  const fetchControl = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const response = await axios.get(
        `${baseURL}/ssp-studio/controls/${controlId}`,
        { withCredentials: true }
      );
      setControl(response.data.data);
    } catch (err) {
      console.error('Error fetching control:', err);
      setError(err.response?.data?.message || err.message);
    }
  };

  useEffect(() => {
    if (control) {
      setFormData({
        implementationStatus: control.implementationStatus || 'planned',
        narrative: control.narrative || '',
        narrativeCompletion: control.narrativeCompletion || 'empty',
        responsibleRole: control.responsibleRole || '',
        implementationNotes: control.implementationNotes || ''
      });
      fetchEvidenceCount();
    }
  }, [control]);

  const fetchEvidenceCount = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const response = await axios.get(
        `${baseURL}/ssp-studio/evidence`,
        {
          params: { sspId: control.ssp_id },
          withCredentials: true
        }
      );

      if (response.data.success) {
        const linkedEvidence = response.data.data.filter(
          e => e.linkedControls && e.linkedControls.includes(control.control_id)
        );
        setEvidenceCount(linkedEvidence.length);
      }
    } catch (err) {
      console.error('Error fetching evidence count:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Check for evidence requirement when selecting "implemented"
    if (name === 'implementationStatus' && value === 'implemented' && evidenceCount === 0) {
      setShowEvidenceWarning(true);
    } else if (name === 'implementationStatus') {
      setShowEvidenceWarning(false);
    }

    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation: Prevent submission if trying to mark as implemented without evidence
    if (formData.implementationStatus === 'implemented' && evidenceCount === 0) {
      setError(`Cannot mark control as "Implemented" without evidence. Please attach evidence artifacts first.`);
      setShowEvidenceWarning(true);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      await axios.put(
        `${baseURL}/ssp-studio/controls/${control._id}`,
        formData,
        { withCredentials: true }
      );

      setSuccess('Control updated successfully!');
      setLoading(false);
    } catch (err) {
      console.error('Error updating control:', err);

      // Handle evidence requirement error
      if (err.response?.data?.code === 'EVIDENCE_REQUIRED') {
        setError(err.response.data.error);
        setShowEvidenceWarning(true);
      } else {
        setError(err.response?.data?.message || 'Failed to update control');
      }
      setLoading(false);
    }
  };

  const handleGenerateNarrative = async () => {
    setLoading(true);
    setError(null);

    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
      const response = await axios.post(
        `${baseURL}/ssp-studio/controls/${control._id}/generate-narrative`,
        {},
        { withCredentials: true }
      );

      setFormData({
        ...formData,
        narrative: response.data.data.narrative || formData.narrative,
        narrativeCompletion: 'draft'
      });
      setSuccess('Narrative generated! Please review and edit as needed.');
      setLoading(false);
    } catch (err) {
      console.error('Error generating narrative:', err);
      setError(err.response?.data?.message || 'Failed to generate narrative');
      setLoading(false);
    }
  };

  return (
    <Container fluid>
      <Row className="mb-4">
        <Col>
          <Button color="secondary" outline size="sm" onClick={() => navigate(`/ssp-studio/ssp/${sspId}/controls`)}>
            <i className="mdi mdi-arrow-left me-1"></i>
            Back to Controls
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert color="success" toggle={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          <Card>
            <CardBody>
              <CardTitle className="mb-4">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-1">{control.control_id}</h5>
                    <p className="text-muted mb-0">{control.control_name}</p>
                  </div>
                  <Badge color="primary">{control.family || 'N/A'}</Badge>
                </div>
              </CardTitle>

              {control.baseline_narrative && (
                <Alert color="info" className="mb-4">
                  <strong>Baseline Guidance:</strong>
                  <div className="mt-2" style={{ fontSize: '0.9em' }}>
                    {control.baseline_narrative}
                  </div>
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="implementationStatus">Implementation Status *</Label>
                      <Input
                        type="select"
                        name="implementationStatus"
                        id="implementationStatus"
                        value={formData.implementationStatus}
                        onChange={handleChange}
                        required
                      >
                        <option value="implemented">Implemented</option>
                        <option value="partial">Partially Implemented</option>
                        <option value="planned">Planned</option>
                        <option value="not-applicable">Not Applicable</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="narrativeCompletion">Narrative Completion</Label>
                      <Input
                        type="select"
                        name="narrativeCompletion"
                        id="narrativeCompletion"
                        value={formData.narrativeCompletion}
                        onChange={handleChange}
                      >
                        <option value="empty">Empty - No narrative yet</option>
                        <option value="draft">Draft - Work in progress</option>
                        <option value="ready">Ready - Complete and reviewed</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Evidence Requirement Warning */}
                {showEvidenceWarning && (
                  <Alert color="warning" className="mt-3">
                    <div className="d-flex align-items-start">
                      <i className="mdi mdi-alert-circle me-2 mt-1" style={{ fontSize: '1.5em' }}></i>
                      <div className="flex-grow-1">
                        <strong>Evidence Required</strong>
                        <p className="mb-2 mt-1">
                          Controls marked as "Implemented" must have at least one linked evidence artifact.
                          This ensures proper documentation and compliance validation.
                        </p>
                        <div>
                          <strong>Current Evidence: </strong>
                          {evidenceCount === 0 ? (
                            <Badge color="danger" className="me-2">None</Badge>
                          ) : (
                            <Badge color="success" className="me-2">{evidenceCount} artifact{evidenceCount !== 1 ? 's' : ''}</Badge>
                          )}
                        </div>
                        {evidenceCount === 0 && (
                          <div className="mt-3">
                            <p className="mb-2 small">
                              <strong>Options:</strong>
                            </p>
                            <ul className="small mb-2">
                              <li>Add evidence from the Evidence Library and link it to this control</li>
                              <li>Select "Partially Implemented" or "Planned" if evidence is not yet available</li>
                            </ul>
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => {
                                // Navigate to evidence tab
                                window.location.hash = '#evidence';
                              }}
                            >
                              <i className="mdi mdi-folder-plus me-1"></i>
                              Go to Evidence Library
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </Alert>
                )}

                <FormGroup>
                  <Label for="responsibleRole">Responsible Role</Label>
                  <Input
                    type="text"
                    name="responsibleRole"
                    id="responsibleRole"
                    value={formData.responsibleRole}
                    onChange={handleChange}
                    placeholder="e.g., System Owner, ISSO, Security Team"
                  />
                </FormGroup>

                <FormGroup>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <Label for="narrative" className="mb-0">Control Implementation Narrative *</Label>
                    <Button
                      type="button"
                      color="info"
                      size="sm"
                      outline
                      onClick={handleGenerateNarrative}
                      disabled={loading}
                    >
                      <i className="mdi mdi-auto-fix me-1"></i>
                      AI Generate
                    </Button>
                  </div>
                  <Input
                    type="textarea"
                    name="narrative"
                    id="narrative"
                    rows="12"
                    value={formData.narrative}
                    onChange={handleChange}
                    placeholder="Describe how this control is implemented in your system..."
                    required
                  />
                  <small className="text-muted">
                    Explain how your organization implements this security control, including specific procedures, tools, and responsible parties.
                  </small>
                </FormGroup>

                <FormGroup>
                  <Label for="implementationNotes">Implementation Notes (Optional)</Label>
                  <Input
                    type="textarea"
                    name="implementationNotes"
                    id="implementationNotes"
                    rows="4"
                    value={formData.implementationNotes}
                    onChange={handleChange}
                    placeholder="Additional notes, caveats, or implementation details..."
                  />
                </FormGroup>

                <div className="mt-4">
                  <Button type="submit" color="primary" disabled={loading}>
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="mdi mdi-content-save me-1"></i>
                        Save Control
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    color="secondary"
                    outline
                    className="ms-2"
                    onClick={() => navigate(`/ssp-studio/ssp/${sspId}/controls`)}
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            </CardBody>
          </Card>
        </Col>

        <Col lg={4}>
          <Card>
            <CardBody>
              <CardTitle>Control Information</CardTitle>
              <div className="mb-3">
                <small className="text-muted d-block">Control ID</small>
                <strong>{control.control_id}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Family</small>
                <strong>{control.family || 'N/A'}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">SSP</small>
                <strong>{control.ssp_id?.name || 'N/A'}</strong>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Linked Evidence</small>
                <div>
                  {evidenceCount > 0 ? (
                    <Badge color="success">
                      <i className="mdi mdi-check-circle me-1"></i>
                      {evidenceCount} artifact{evidenceCount !== 1 ? 's' : ''}
                    </Badge>
                  ) : (
                    <Badge color="warning">
                      <i className="mdi mdi-alert me-1"></i>
                      No evidence
                    </Badge>
                  )}
                </div>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block">Last Updated</small>
                <strong>
                  {control.updatedAt ? new Date(control.updatedAt).toLocaleDateString() : 'Never'}
                </strong>
              </div>
            </CardBody>
          </Card>

          {control.evidenceLinks && control.evidenceLinks.length > 0 && (
            <Card className="mt-3">
              <CardBody>
                <CardTitle>
                  Linked Evidence ({control.evidenceLinks.length})
                </CardTitle>
                <Table size="sm">
                  <tbody>
                    {control.evidenceLinks.map((evidence, idx) => (
                      <tr key={idx}>
                        <td>
                          <i className="mdi mdi-file-document text-muted me-1"></i>
                          {evidence.title || 'Evidence'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          )}

          <Card className="mt-3">
            <CardBody>
              <CardTitle>Quick Actions</CardTitle>
              <div className="d-grid gap-2">
                <Button color="light" size="sm" outline>
                  <i className="mdi mdi-paperclip me-1"></i>
                  Attach Evidence
                </Button>
                <Button color="light" size="sm" outline>
                  <i className="mdi mdi-history me-1"></i>
                  View History
                </Button>
                <Button color="light" size="sm" outline>
                  <i className="mdi mdi-export me-1"></i>
                  Export Control
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ControlDetail;
